import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  useVmetaCollections,
  useDeleteVmetaCollection,
  useCheckVmetaCollectionWritePermission,
  type CheckVmetaCollectionWritePermissionResponseBody,
} from "@via/pages/projects/metadata/metadata.api.tsx";
import Table, { DrawerSize } from "@via/components/table/table";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { formatDateString } from "@via/utils/helpers";
import TableContextMenu from "@via/components/table/table-context-menu";
import {
  BUTTONS,
  METADATA_CONFIGURATION_TABLE,
  METADATA_CONFIGURATION_TABS,
  ROUTES,
} from "@via/utils/constants";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import { ActionButton } from "@via/components/action-button/action-button";
import Drawer from "@via/components/drawer";
import CreateCollectionForm from "@via/pages/projects/metadata/configuration/collections/create-collection-form";
import EditCollectionForm from "@via/pages/projects/metadata/configuration/collections/edit-collection-form";
import { VmetaCollection } from "common";
import { createOneLineColumnFactory } from "@via/utils/table-utils";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { useLocation } from "react-router-dom";

interface CollectionsProps {
  projectId: string;
  activeTab?: string;
  onCollectionClick?: (collection: VmetaCollection) => void;
}

type VmetaCollectionWithOtherFields = VmetaCollection & {
  owner: {
    username: string;
  };
  createdAt: Date;
  updatedAt: Date;
};

const columnHelper = createColumnHelper<VmetaCollectionWithOtherFields>();

const Collections: FC<CollectionsProps> = ({
  projectId,
  activeTab,
  onCollectionClick,
}) => {
  const location = useLocation();
  const createOneLineColumn = createOneLineColumnFactory(columnHelper);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [collectionToBeDeleted, setCollectionToBeDeleted] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [isCreateCollectionDrawerOpen, setIsCreateCollectionDrawerOpen] =
    useState(false);
  const [collectionToBeEdited, setCollectionToBeEdited] =
    useState<VmetaCollection | null>(null);
  const [deletePermissionResult, setDeletePermissionResult] =
    useState<CheckVmetaCollectionWritePermissionResponseBody | null>(null);

  const {
    mutateAsync: deleteVmetaCollection,
    isPending: isDeleteVmetaCollectionLoading,
  } = useDeleteVmetaCollection();

  const {
    mutateAsync: checkCollectionWritePermission,
    isPending: isCheckCollectionWritePermissionLoading,
  } = useCheckVmetaCollectionWritePermission();

  const { data: collections, isLoading: isCollectionsLoading } =
    useVmetaCollections(
      page,
      pageSize,
      [],
      [
        {
          id: "projectID",
          value: projectId,
        },
      ]
    );

  const handleContextMenuAction = async (
    optionName: string,
    collection: VmetaCollection
  ): Promise<void> => {
    switch (optionName) {
      case "Edit Collection":
        setCollectionToBeEdited(collection);
        return;
      case "Delete Collection":
        if (!collection.writeAllowed) return;
        setCollectionToBeDeleted(collection);
        setDeletePermissionResult(null);
        return;
    }
  };

  const getRowOptions = useCallback(
    (collection: VmetaCollectionWithOtherFields) => {
      const canWrite = !!collection.writeAllowed;
      return METADATA_CONFIGURATION_TABLE.filter(opt => {
        if (
          opt.name === "Edit Collection" ||
          opt.name === "Delete Collection"
        ) {
          return canWrite;
        }
        return true;
      });
    },
    []
  );

  useEffect(() => {
    const id = collectionToBeDeleted?._id;
    if (!id) return;

    (async () => {
      try {
        const res = await checkCollectionWritePermission({ id: String(id) });
        setDeletePermissionResult(res);
      } catch (e) {
        // If check fails, fall back to showing regular delete confirm
        setDeletePermissionResult(null);
        console.error("Error checking collection write permission:", e);
      }
    })();
  }, [collectionToBeDeleted, checkCollectionWritePermission]);

  const columns = [
    columnHelper.accessor("name", {
      cell: info => {
        const name = info.getValue();
        const collection = info.row.original;
        return onCollectionClick ? (
          <button
            type="button"
            onClick={() => onCollectionClick(collection)}
            className="via-link whitespace-nowrap px-1">
            {name}
          </button>
        ) : (
          <span>{name}</span>
        );
      },
      header: () => <span className="px-1">Name</span>,
    }),
    createOneLineColumn("label", "Label"),
    createOneLineColumn("dataDeleteProtected", "Deletion Protection", {
      cellRenderer: value => value?.toString(),
    }),
    columnHelper.accessor("_id", {
      id: "collectionId",
      cell: info => (
        <span className="whitespace-nowrap px-1">{info.getValue()}</span>
      ),
      header: () => <span className="px-1">Id</span>,
    }),
    createOneLineColumn("owner.username", "Owner"),
    createOneLineColumn("createdAt", "Created At", {
      cellRenderer: value => formatDateString(value as Date, "LLL"),
    }),
    createOneLineColumn("updatedAt", "Updated At", {
      cellRenderer: value => formatDateString(value as Date, "LLL"),
    }),

    columnHelper.accessor("_id", {
      id: "options",
      header: () => "Options",
      cell: ({ row }) => {
        const options = getRowOptions(row.original);
        if (options.length === 0) return null;
        return (
          <TableContextMenu
            options={options}
            onSelect={option => {
              void handleContextMenuAction(option.name, row.original);
            }}
          />
        );
      },
      enableSorting: false,
    }),
  ] as Array<ColumnDef<VmetaCollectionWithOtherFields>>;

  const routePattern = useMemo(() => {
    if (location.pathname.startsWith("/sample-trackers/")) {
      return ROUTES.SAMPLE_TRACKERS_DETAIL;
    }
    return ROUTES.PROJECT_METADATA;
  }, [location.pathname]);

  const getAllCollectionsContext = useCallback(() => {
    const collectionsList = (collections?.data ||
      []) as VmetaCollectionWithOtherFields[];

    return {
      project: {
        id: projectId,
      },
      collections: collectionsList.map(collection => {
        const collectionWithFields =
          collection as VmetaCollectionWithOtherFields;
        return {
          id: collection._id,
          name: collection.name,
          label: collection.label,
          dataDeleteProtected: collection.dataDeleteProtected,
          owner: collectionWithFields.owner?.username || collection.owner || "",
          createdAt: collectionWithFields.createdAt || collection.creationDate,
          updatedAt:
            collectionWithFields.updatedAt || collection.lastUpdateDate,
        };
      }),
      total: collections?.results || 0,
      currentPage: page,
      pageSize: pageSize,
    };
  }, [projectId, collections, page, pageSize]);

  useRegisterCortexContext({
    routePattern,
    contextName: "All Collections Configuration",
    getContext: getAllCollectionsContext,
    systemPrompt:
      "You are analyzing metadata collections configuration page. Help answer questions about collections, their configuration, fields, and management.",
    enabled:
      !!collections &&
      activeTab === METADATA_CONFIGURATION_TABS.ALL_COLLECTIONS,
  });

  return (
    <>
      <div className="flex justify-end -mt-6 relative top-11">
        <ActionButton
          button={BUTTONS.NEW_METADATA_COLLECTION}
          onClick={() => {
            setIsCreateCollectionDrawerOpen(true);
          }}
        />
      </div>
      <div className="mt-6 overflow-auto h-100vh">
        <Table
          data={collections?.data}
          isLoading={isCollectionsLoading}
          columns={columns}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageSizeOptions={[100]}
          total={collections?.results || 0}
          enableGlobalFilter
          applyColumnSize
          customTableClassName="w-max whitespace-nowrap"
          emptyMessage="No data available"
        />
      </div>
      {collectionToBeDeleted && (
        <DeleteModal
          item={collectionToBeDeleted}
          setItem={setCollectionToBeDeleted}
          deleteFunction={() =>
            deleteVmetaCollection({ id: collectionToBeDeleted._id as string })
          }
          isLoading={
            isDeleteVmetaCollectionLoading ||
            isCheckCollectionWritePermissionLoading
          }
          title="Delete Collection"
          confirmText="Delete Collection"
          hideConfirmButton={
            deletePermissionResult
              ? !deletePermissionResult.data.writeAllowed
              : false
          }
          message={name => {
            if (
              isCheckCollectionWritePermissionLoading &&
              !deletePermissionResult
            ) {
              return <>Checking permissions…</>;
            }

            if (
              deletePermissionResult &&
              !deletePermissionResult.data.writeAllowed
            ) {
              return (
                <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                  <div className="mt-1">
                    You don&apos;t have permission to delete{" "}
                    <strong className="font-semibold">{name}</strong>.
                  </div>
                </div>
              );
            }

            const datasetNames = deletePermissionResult?.data?.inUseDatasets
              ?.length
              ? Array.from(
                  new Set(
                    deletePermissionResult.data.inUseDatasets
                      .map(d => d.name || d.id)
                      .filter(Boolean)
                  )
                )
              : [];
            const datasetCount = datasetNames.length;
            const isFileCollection = name === "file";

            return (
              <>
                Are you sure you want to delete{" "}
                <strong className="font-semibold">{name}</strong>?
                <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                  <div className="font-semibold">Warning</div>
                  <div className="mt-1">
                    Deleting <strong className="font-semibold">{name}</strong>{" "}
                    is permanent. This action is not recoverable.
                  </div>

                  <div className="mt-3 font-semibold">What will be deleted</div>
                  <div className="mt-1">
                    All data inside this collection will be removed.
                  </div>

                  {isFileCollection ? (
                    <>
                      <div className="mt-3 font-semibold">
                        What will be affected
                      </div>
                      {datasetCount > 0 ? (
                        <>
                          <div className="mt-1">
                            This collection is referenced by{" "}
                            <strong className="font-semibold">
                              {datasetCount}
                            </strong>{" "}
                            datasets. If you delete it, file data will be
                            removed from those datasets.
                          </div>
                          <details className="mt-2">
                            <summary className="cursor-pointer select-none">
                              <span className="font-semibold">
                                Affected datasets ({datasetCount})
                              </span>{" "}
                              (expand to view list)
                            </summary>
                            <div className="mt-2 text-red-900">
                              {datasetNames.map(ds => (
                                <div key={ds}>{ds}</div>
                              ))}
                            </div>
                          </details>
                        </>
                      ) : (
                        <div className="mt-1">
                          No datasets will be affected by deleting this
                          collection.
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              </>
            );
          }}
        />
      )}
      <Drawer
        id="create-collection"
        open={isCreateCollectionDrawerOpen}
        setOpen={setIsCreateCollectionDrawerOpen}
        size={DrawerSize.Big}>
        <CreateCollectionForm
          setOpen={setIsCreateCollectionDrawerOpen}
          projectId={projectId}
        />
      </Drawer>
      <Drawer
        id="edit-collection"
        open={!!collectionToBeEdited}
        setOpen={() => setCollectionToBeEdited(null)}
        size={DrawerSize.Big}>
        <EditCollectionForm
          setOpen={() => setCollectionToBeEdited(null)}
          projectId={projectId}
          collection={collectionToBeEdited}
        />
      </Drawer>
    </>
  );
};

export default Collections;
